import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { identifyPatient, PracticeInfo } from '../api/bookingApi';
import LoadingState from './LoadingState';

interface PhoneStepProps {
  clientId: string;
  practiceInfo: PracticeInfo | null;
  onIdentified: (r: { phone: string; exists: boolean }) => void;
}

export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function PhoneStep({ clientId, practiceInfo, onIdentified }: PhoneStepProps) {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const rawDigits = phone.replace(/\D/g, '');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rawDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const result = await identifyPatient(clientId, rawDigits);
      onIdentified({ phone: rawDigits, exists: result.exists });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { 
        staggerChildren: 0.1, 
        delayChildren: 0.2,
        type: "tween" as const
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        type: "spring" as const, 
        stiffness: 100 
      } 
    }
  };

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <LoadingState key="loading" type="verification" message="Identifying Account" />
      ) : (
        <motion.div 
          key="content"
          variants={container} 
          initial="hidden" 
          animate="show"
          exit={{ opacity: 0, y: -20 }}
        >
          <div className="text-center mb-6 sm:mb-8">
            <motion.div
              className="relative w-28 h-28 sm:w-40 sm:h-40 mx-auto mb-2 flex items-center justify-center"
              variants={item}
            >
              {/* Layered Spring Animation */}
              <motion.div 
                className="absolute inset-0 bg-primary/10 rounded-full"
                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <svg
                  className="w-16 h-16 sm:w-24 sm:h-24 text-primary drop-shadow-[0_10px_15px_rgba(38,173,208,0.3)]"
                  viewBox="0 0 100 100"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ transform: 'rotateX(25deg) rotateY(-25deg)' }}
                >
                  {/* Modern Calendar with depth layers */}
                  <motion.rect 
                    x="10" y="25" width="80" height="65" rx="12" 
                    stroke="currentColor" strokeWidth="6"
                    initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                    transition={{ duration: 1, delay: 0.5 }}
                  />
                  <rect x="10" y="25" width="80" height="22" rx="6" fill="currentColor" opacity="0.15" />
                  <path d="M30 12V30M70 12V30" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
                  <motion.rect 
                    x="25" y="52" width="14" height="14" rx="4" fill="currentColor"
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1 }}
                  />
                  <rect x="43" y="52" width="14" height="14" rx="4" fill="currentColor" opacity="0.3" />
                  <rect x="61" y="52" width="14" height="14" rx="4" fill="currentColor" opacity="0.3" />
                </svg>
              </motion.div>
            </motion.div>

            <motion.h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight" variants={item}>
              Book an Appointment
            </motion.h2>
            {practiceInfo?.name && (
              <motion.p className="text-gray-500 mt-2 text-base sm:text-lg font-medium" variants={item}>
                at {practiceInfo.name}
              </motion.p>
            )}
          </div>

          <motion.form onSubmit={handleSubmit} className="space-y-6" variants={item}>
            <div className="space-y-2">
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 ml-1">
                Mobile phone number
              </label>
              <input
                id="phone"
                type="tel"
                className="input-field !h-12 sm:!h-14 !text-base sm:!text-lg !px-4 sm:!px-5"
                placeholder="(555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                autoComplete="tel"
                inputMode="numeric"
                required
              />
              {error && <p className="text-destructive text-sm font-medium mt-2 ml-1 animate-shake">{error}</p>}
            </div>

            <button type="submit" className="btn-primary !h-12 sm:!h-14 !text-base sm:!text-lg" disabled={loading}>
              Continue
            </button>
          </motion.form>

          <motion.p className="text-sm text-gray-500 text-center mt-8 font-medium px-4" variants={item}>
            Quick and secure booking. We'll find your records instantly.
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
